import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getCurrencyInfo } from 'Redux/actions/auth'
import { getCampaignBudgetRange } from 'Redux/actions/scheduleActions'
import {
  getCampaignToSchedule,
  getAmountOfCampaigns,
} from 'Redux/selectors/campaigns'
import { getUserSelector } from 'Redux/selectors/auth'
import { Slider } from 'Containers/ui/SliderSelect'
import { LoadingSpinner } from 'Containers/ui/LoadingSpinner'
import { Mobile, Desktop } from 'Shared/components/Responsive'
import successMessageBubble from 'Shared/img/svg/success-message-bubble.svg'
import warningMessageBubble from 'Shared/img/Warning.svg'
import { CampaignToSchedule, CampaignPlatformTypeEnum } from 'Types/index'
import { msgLowReachReceived } from 'Helpers/tagManager'
import { IconQuestionMark } from 'Stories/Icon/Icon.stories'
import { WhiteGreenBtn } from 'Stories/Button/Button.stories'
import { Hint } from 'Stories/Hint/Hint.stories'
import { HintGuide } from 'Stories/HintGuide/HintGuide.stories'
import SetReachHint from 'Containers/CreateCampaignPage/components/SetReachHint'
import SetReachHintModal from 'Containers/CreateCampaignPage/components/SetReachHintModal'
import { HINT_SET_REACH, CLEAR_HINT } from 'Constants/hints'

import { colors } from 'Styled/variables'

import { useBudget } from '../../hooks'

import { ReachSettingsProps } from './props'
import {
  Container,
  SectionTitle,
  SectionTitleMain,
  SliderContainer,
  EstimatedReach,
  EstimatedReachLabel,
  EstimatedReachValue,
  EstimatedReachExplanation,
  EstimatedReachExplanationText,
  EstimatedReachIcon,
  EditGenresAndLanguagesButton,
  AlertContainer,
  EstimationWrapper,
  Notice,
  CampaignBudget,
  CampaignBudgetLabel,
  CampaignBudgetAmount,
  MatchingBlock,
  MatchingBlockHeader,
  MatchingBlockValue,
  MatchingBlockValueMain,
  MatchingBlockValueSecond,
  CenteredBlock,
  HintModal,
} from './styles'

export const ReachSettings: React.FC<ReachSettingsProps> = ({
  goToPrevStep,
  budget,
  setBudget,
  setIsContinueActive,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const urlParts: Array<string> = pathname.split('/')
  const scheduleIndexPartUrl = urlParts.findIndex(
    urlPart => urlPart === 'schedule'
  )
  const campaignID = scheduleIndexPartUrl && urlParts[scheduleIndexPartUrl + 1]
  const {
    sliderParameters: { min, max, step },
    currencySymbol,
  } = useBudget({})

  const campaignDetails: CampaignToSchedule = useSelector(getCampaignToSchedule)
  const amountOfCampaigns = useSelector(getAmountOfCampaigns)
  const user = useSelector(getUserSelector)
  const [componentDidMount, setComponentDidMount] = useState(false)
  const [isBudgetProper, setIsBudgetProper] = useState(false)
  const [hintGuide, setHint] = useState({
    component: null,
    name: null,
    isLeft: true,
  })
  const [isModalSetReach, setIsModalValueSetReach] = useState(false)

  const handleGetBudgetRange = async () => {
    dispatch(
      getCampaignBudgetRange(campaignDetails.id, error => {
        if (error) {
          msgLowReachReceived(
            campaignDetails.type,
            CampaignPlatformTypeEnum.SPOTIFY,
            campaignDetails.type === 'first' ? 1 : amountOfCampaigns,
            campaignDetails.id,
            user.id,
            campaignDetails?.genres.map(genre => genre.name).join(),
            campaignDetails?.languages.map(language => language.name).join(),
            campaignDetails.is_feed || false
          )
        }
      })
      // @ts-ignore
    ).then(() => {
      setIsBudgetProper(true)
    })
  }

  useEffect(() => {
    if (!componentDidMount) {
      dispatch(getCurrencyInfo())
      if (campaignID === campaignDetails.id) {
        handleGetBudgetRange()
        setComponentDidMount(true)
      }
    }
  }, [componentDidMount, campaignDetails])

  useEffect(() => {
    if (budget && max) {
      setIsContinueActive(true)
    } else {
      setIsContinueActive(false)
    }
  }, [budget])

  useEffect(() => {
    if (min && max && !budget && isBudgetProper) {
      setBudget(
        Math.round((Math.round((max / 2 + min / 2) / step) * step) / 10) * 10
      )
    }
  }, [min, max, budget, isBudgetProper])

  const isEmptyRange = max === 0
  const isMinEqualsMax = max === min

  const handleSetHint = ({
    hint,
    isLeft,
  }: {
    hint: string
    isLeft: boolean
  }) => {
    if (hint === HINT_SET_REACH) {
      // @ts-ignore
      setHint({ component: <SetReachHint />, name: HINT_SET_REACH, isLeft })
    }

    if (hint === CLEAR_HINT) {
      // @ts-ignore
      setHint({ component: null, name: null })
    }
  }

  return (
    <>
      {hintGuide && hintGuide.component && (
        <HintModal left={hintGuide.isLeft}>
          {hintGuide.component}
          <CenteredBlock>
            <WhiteGreenBtn
              color="white"
              onClick={() =>
                handleSetHint({ hint: 'clearHint', isLeft: false })
              }
            >
              {t('scheduleCampaignPage.gotIt')}
            </WhiteGreenBtn>
          </CenteredBlock>
        </HintModal>
      )}
      <Container isEmptyRange={isEmptyRange}>
        {isBudgetProper ? (
          <>
            {!!max && (
              <>
                {!isMinEqualsMax ? (
                  <>
                    <SectionTitleMain>
                      {t('scheduleCampaignPage.chooseCampaignBudget')}
                    </SectionTitleMain>
                    <Mobile>
                      {isBudgetProper && (
                        <AlertContainer
                          isEmptyRange={isEmptyRange}
                          isMinEqualsMax={isMinEqualsMax}
                        >
                          <Notice isEmptyRange={isEmptyRange}>
                            {t('scheduleCampaignPage.notice')}
                          </Notice>
                          <EstimatedReachExplanation
                            isEmptyRange={isEmptyRange}
                          >
                            <EstimatedReachIcon
                              src={
                                !max
                                  ? warningMessageBubble
                                  : successMessageBubble
                              }
                              alt=""
                            />
                            <EstimatedReachExplanationText
                              isEmptyRange={isEmptyRange}
                            >
                              {!max
                                ? t('scheduleCampaignPage.reachAlertWarning')
                                : t('scheduleCampaignPage.reachAlertSuccess')}
                            </EstimatedReachExplanationText>
                          </EstimatedReachExplanation>
                          <EditGenresAndLanguagesButton
                            isEmptyRange={isEmptyRange}
                            onClick={goToPrevStep}
                          >
                            {t('scheduleCampaignPage.editGenresAndLanguages')}
                          </EditGenresAndLanguagesButton>
                        </AlertContainer>
                      )}
                    </Mobile>
                    <SliderContainer>
                      {budget && (
                        <Slider
                          min={min}
                          max={max}
                          initialValue={budget}
                          currency={currencySymbol}
                          step={step}
                          mobileSideContent={
                            <EstimationWrapper isMinEqualsMax={isMinEqualsMax}>
                              <SectionTitle>
                                {t('scheduleCampaignPage.totalAudienceSize')}
                              </SectionTitle>
                              <EstimatedReach>
                                <MatchingBlock>
                                  <MatchingBlockHeader>
                                    {campaignDetails.is_feed
                                      ? t(
                                          'scheduleCampaignPage.playlistsMatched'
                                        )
                                      : t(
                                          'scheduleCampaignPage.totalPlaylistsMatched'
                                        )}
                                    {campaignDetails.is_feed ? (
                                      <>
                                        <Desktop>
                                          <HintGuide
                                            hintName="hintSetReach"
                                            setHintName={hint =>
                                              handleSetHint({
                                                hint,
                                                isLeft: true,
                                              })
                                            }
                                            currentHint={
                                              hintGuide && hintGuide.name
                                            }
                                          />
                                        </Desktop>
                                        <Mobile>
                                          <Hint
                                            // @ts-ignore
                                            onClick={setIsModalValueSetReach}
                                          >
                                            <IconQuestionMark />
                                          </Hint>
                                          <SetReachHintModal
                                            isOpen={isModalSetReach}
                                            toggle={setIsModalValueSetReach}
                                          />
                                        </Mobile>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </MatchingBlockHeader>
                                  <MatchingBlockValue>
                                    <MatchingBlockValueMain>
                                      {campaignDetails.is_feed
                                        ? `${campaignDetails.minPlaylistsCount ||
                                            0}-${campaignDetails.maxPlaylistsCount ||
                                            0}`
                                        : campaignDetails.playlistsCount}
                                    </MatchingBlockValueMain>
                                  </MatchingBlockValue>
                                </MatchingBlock>
                                <MatchingBlock>
                                  <MatchingBlockHeader>
                                    {campaignDetails.is_feed
                                      ? t('scheduleCampaignPage.curatorsReach')
                                      : t(
                                          'scheduleCampaignPage.totalCuratorsReach'
                                        )}
                                    {campaignDetails.is_feed ? (
                                      <>
                                        <Desktop>
                                          <HintGuide
                                            hintName="hintSetReach"
                                            setHintName={hint =>
                                              handleSetHint({
                                                hint,
                                                isLeft: false,
                                              })
                                            }
                                            currentHint={
                                              hintGuide && hintGuide.name
                                            }
                                          />
                                        </Desktop>
                                        <Mobile>
                                          <Hint
                                            // @ts-ignore
                                            onClick={setIsModalValueSetReach}
                                          >
                                            <IconQuestionMark />
                                          </Hint>
                                          <SetReachHintModal
                                            isOpen={isModalSetReach}
                                            toggle={setIsModalValueSetReach}
                                          />
                                        </Mobile>
                                      </>
                                    ) : (
                                      <></>
                                    )}
                                  </MatchingBlockHeader>
                                  <MatchingBlockValue>
                                    <MatchingBlockValueMain>
                                      {campaignDetails.is_feed
                                        ? `${campaignDetails.minCuratorsCount ||
                                            0}-${campaignDetails.maxCuratorsCount ||
                                            0}`
                                        : campaignDetails.curatorsCount}
                                    </MatchingBlockValueMain>
                                  </MatchingBlockValue>
                                </MatchingBlock>
                              </EstimatedReach>
                            </EstimationWrapper>
                          }
                          onChange={setBudget}
                          onDebounce={setIsContinueActive}
                        />
                      )}
                    </SliderContainer>
                  </>
                ) : (
                  <CampaignBudget>
                    <CampaignBudgetLabel>
                      {t('scheduleCampaignPage.campaignBudget')}
                    </CampaignBudgetLabel>
                    <CampaignBudgetAmount>{`${currencySymbol} ${budget}`}</CampaignBudgetAmount>
                  </CampaignBudget>
                )}
                <Desktop>
                  <EstimationWrapper isMinEqualsMax={isMinEqualsMax}>
                    <SectionTitle>
                      {t('scheduleCampaignPage.totalAudienceSize')}
                    </SectionTitle>
                    <EstimatedReach>
                      <MatchingBlock>
                        <MatchingBlockHeader>
                          {campaignDetails.is_feed
                            ? t('scheduleCampaignPage.playlistsMatched')
                            : t('scheduleCampaignPage.totalPlaylistsMatched')}
                          {campaignDetails.is_feed ? (
                            <>
                              <Desktop>
                                <HintGuide
                                  hintName="hintSetReach"
                                  setHintName={hint =>
                                    handleSetHint({
                                      hint,
                                      isLeft: true,
                                    })
                                  }
                                  currentHint={hintGuide && hintGuide.name}
                                />
                              </Desktop>
                              <Mobile>
                                {/* @ts-ignore */}
                                <Hint onClick={setIsModalValueSetReach}>
                                  <IconQuestionMark />
                                </Hint>
                                <SetReachHintModal
                                  isOpen={isModalSetReach}
                                  toggle={setIsModalValueSetReach}
                                />
                              </Mobile>
                            </>
                          ) : (
                            <></>
                          )}
                        </MatchingBlockHeader>
                        <MatchingBlockValue>
                          <MatchingBlockValueMain>
                            {campaignDetails.is_feed
                              ? `${campaignDetails.minPlaylistsCount ||
                                  0}-${campaignDetails.maxPlaylistsCount || 0}`
                              : campaignDetails.playlistsCount}
                          </MatchingBlockValueMain>
                        </MatchingBlockValue>
                      </MatchingBlock>
                      <MatchingBlock>
                        <MatchingBlockHeader>
                          {campaignDetails.is_feed
                            ? t('scheduleCampaignPage.curatorsReach')
                            : t('scheduleCampaignPage.totalCuratorsReach')}
                          {campaignDetails.is_feed ? (
                            <>
                              <Desktop>
                                <HintGuide
                                  hintName="hintSetReach"
                                  setHintName={hint =>
                                    handleSetHint({
                                      hint,
                                      isLeft: false,
                                    })
                                  }
                                  currentHint={hintGuide && hintGuide.name}
                                />
                              </Desktop>
                              <Mobile>
                                {/* @ts-ignore */}
                                <Hint onClick={setIsModalValueSetReach}>
                                  <IconQuestionMark />
                                </Hint>
                                <SetReachHintModal
                                  isOpen={isModalSetReach}
                                  toggle={setIsModalValueSetReach}
                                />
                              </Mobile>
                            </>
                          ) : (
                            <></>
                          )}
                        </MatchingBlockHeader>
                        <MatchingBlockValue>
                          <MatchingBlockValueMain>
                            {campaignDetails.is_feed
                              ? `${campaignDetails.minCuratorsCount ||
                                  0}-${campaignDetails.maxCuratorsCount || 0}`
                              : campaignDetails.curatorsCount}
                          </MatchingBlockValueMain>
                        </MatchingBlockValue>
                      </MatchingBlock>
                    </EstimatedReach>
                  </EstimationWrapper>
                </Desktop>
              </>
            )}
          </>
        ) : (
          <LoadingSpinner color={colors.green} size="large" />
        )}
        <Desktop>
          {isBudgetProper && (
            <AlertContainer
              isEmptyRange={isEmptyRange}
              isMinEqualsMax={isMinEqualsMax}
            >
              <Notice isEmptyRange={isEmptyRange}>
                {t('scheduleCampaignPage.notice')}
              </Notice>
              <EstimatedReachExplanation isEmptyRange={isEmptyRange}>
                <EstimatedReachIcon
                  src={!max ? warningMessageBubble : successMessageBubble}
                  alt=""
                />
                <EstimatedReachExplanationText isEmptyRange={isEmptyRange}>
                  {!max
                    ? t('scheduleCampaignPage.reachAlertWarning')
                    : t('scheduleCampaignPage.reachAlertSuccess')}
                </EstimatedReachExplanationText>
              </EstimatedReachExplanation>
              <EditGenresAndLanguagesButton
                isEmptyRange={isEmptyRange}
                onClick={goToPrevStep}
              >
                {t('scheduleCampaignPage.editGenresAndLanguages')}
              </EditGenresAndLanguagesButton>
            </AlertContainer>
          )}
        </Desktop>
      </Container>
    </>
  )
}
